import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { 
  TrendingUp, TrendingDown, DollarSign, Brain, AlertTriangle,
  Building2, Calendar, PieChart, BarChart3, Activity,
  Briefcase, CreditCard, Eye, Package, Users, ChevronDown,
  FileText, Sparkles, Download, RefreshCw
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart as RePieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ComposedChart, Area, Treemap, Sankey
} from 'recharts';
import { UserProfile } from '@shared/types';

interface FinancialDashboardProps {
  user: UserProfile | null;
}

// Real financial data from provided spreadsheet
const financialData = {
  locations: [
    { code: 'JCITY', name: 'Johnson City', revenue: 322407, netIncome: 8986, margin: 2.8 },
    { code: 'RVD', name: 'Riverdale', revenue: 142303, netIncome: -8008, margin: -5.6 },
    { code: 'ECOBB', name: 'East Cobb', revenue: 188659, netIncome: -51430, margin: -27.3 },
    { code: 'CTN', name: 'Canton', revenue: 21305, netIncome: -82452, margin: -211.0 },
    { code: 'LVL', name: 'Lawrenceville', revenue: 252475, netIncome: -40153, margin: -15.9 }
  ],
  totalRevenue: 927150,
  annualProjection: 1950000,
  incomeBreakdown: {
    patientCollections: 629727,
    insuranceCollections: 291007,
    medicalInsurance: 133468,
    visionInsurance: 155905
  },
  expenses: {
    costOfLabor: 495189,
    costOfGoods: 209193,
    totalCOGSCOL: 855651,
    operatingExpenses: 346927,
    otherExpenses: 51817
  },
  metrics: {
    grossMargin: 24.0,
    cogsColPercentage: 92.3,
    netMargin: -22.9,
    targetNetMargin: 20.0,
    targetCOGSCOL: 60.0
  }
};

const FinancialDashboard: React.FC<FinancialDashboardProps> = ({ user }) => {
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [timeRange, setTimeRange] = useState('ytd');
  const [showAIInsights] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // AI-generated insights based on financial data
  const aiInsights = useMemo(() => ({
    critical: [
      {
        title: 'Severe Margin Crisis',
        value: '-22.9% Net Margin',
        impact: 'Missing 20% target by 42.9 points',
        recommendation: 'Immediate cost reduction required - payroll at 53.4% vs 29% target'
      },
      {
        title: 'COGS/COL Overrun',
        value: '92.3% of Revenue',
        impact: 'Exceeding 60% target by 32.3 points',
        recommendation: 'Labor optimization critical - $166K annual savings potential'
      },
      {
        title: 'Location Hemorrhaging',
        value: '4 of 5 Locations Losing Money',
        impact: 'CTN at -211% margin, ECOBB at -27.3%',
        recommendation: 'Consider closing CTN, restructure ECOBB operations'
      }
    ],
    opportunities: [
      {
        title: 'Insurance Mix Optimization',
        value: '31.4% Insurance Revenue',
        impact: 'Could increase to 40% industry standard',
        recommendation: 'Credential more providers, improve billing processes'
      },
      {
        title: 'Optical Margin Improvement',
        value: '9.1% Optical COGS',
        impact: 'Below 14% industry average',
        recommendation: 'Increase frame markups, negotiate lab costs'
      }
    ]
  }), []);

  // KPI Card Component
  const KPICard = ({ title, value, change, icon: Icon, status, subtitle }: any) => (
    <Card className="bg-slate-900 border-slate-700 hover:border-slate-600 transition-all">
      <CardContent className="p-4">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wide">{title}</p>
            <p className="text-2xl font-bold text-white mt-1">{value}</p>
            {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
          </div>
          <div className="flex flex-col items-end">
            <Icon className={`w-5 h-5 ${
              status === 'good' ? 'text-green-400' : 
              status === 'warning' ? 'text-yellow-400' : 'text-red-400'
            }`} />
            {change && (
              <Badge className={`mt-2 text-xs ${
                change > 0 ? 'bg-green-900/50 text-green-400 border-green-700' : 
                'bg-red-900/50 text-red-400 border-red-700'
              }`}>
                {change > 0 ? '+' : ''}{change}%
              </Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Revenue Waterfall Chart
  const WaterfallChart = () => {
    const waterfallData = [
      { name: 'Gross Revenue', value: 927150, type: 'positive' },
      { name: 'Cost of Labor', value: -495189, type: 'negative' },
      { name: 'COGS', value: -209193, type: 'negative' },
      { name: 'Operating Expenses', value: -346927, type: 'negative' },
      { name: 'Other Expenses', value: -51817, type: 'negative' },
      { name: 'Net Income', value: -175976, type: 'total' }
    ];

    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={waterfallData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
          <XAxis dataKey="name" stroke="#94a3b8" angle={-45} textAnchor="end" height={80} />
          <YAxis stroke="#94a3b8" tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`} />
          <Tooltip 
            contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
            formatter={(value: any) => `$${(value / 1000).toFixed(0)}K`}
          />
          <Bar dataKey="value">
            {waterfallData.map((d, i) => (
              <Cell
                key={`wf-${i}`}
                fill={d.type === 'positive' ? '#10b981' : d.type === 'negative' ? '#ef4444' : '#3b82f6'}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  };

  // AI Insights Panel
  const AIInsightsPanel = () => (
    <Card className="bg-gradient-to-br from-indigo-900/20 to-purple-900/20 border-indigo-700/50">
      <CardHeader>
        <CardTitle className="flex items-center text-white">
          <Brain className="w-5 h-5 mr-2 text-indigo-400" />
          CFO AI Analysis: Financial Health Assessment
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <h4 className="text-red-400 font-semibold mb-2 flex items-center">
            <AlertTriangle className="w-4 h-4 mr-1" />
            Critical Financial Issues
          </h4>
          <div className="space-y-2">
            {aiInsights.critical.map((insight, i) => (
              <div key={i} className="bg-red-900/20 border border-red-700/50 rounded p-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="text-white font-medium">{insight.title}</p>
                    <p className="text-sm text-red-400">{insight.value} - {insight.impact}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      <Sparkles className="w-3 h-3 inline mr-1" />
                      {insight.recommendation}
                    </p>
                  </div>
                  <Badge className="bg-red-900/50 text-red-400 border-red-700">
                    URGENT
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-green-400 font-semibold mb-2 flex items-center">
            <TrendingUp className="w-4 h-4 mr-1" />
            Revenue Opportunities
          </h4>
          <div className="space-y-2">
            {aiInsights.opportunities.map((insight, i) => (
              <div key={i} className="bg-green-900/20 border border-green-700/50 rounded p-3">
                <div className="flex-1">
                  <p className="text-white font-medium">{insight.title}</p>
                  <p className="text-sm text-green-400">{insight.value} - {insight.impact}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    <Sparkles className="w-3 h-3 inline mr-1" />
                    {insight.recommendation}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/50 backdrop-blur">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-400 to-purple-600 bg-clip-text text-transparent">
                Financial Command Center
              </h1>
              <p className="text-slate-400 mt-1">PE-Grade Financial Analytics & Forecasting</p>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRefreshing(true)}
                disabled={refreshing}
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Sync QuickBooks
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Financials
              </Button>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="flex gap-3">
            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger className="w-48 bg-slate-800 border-slate-700 text-white">
                <Building2 className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {financialData.locations.map(loc => (
                  <SelectItem key={loc.code} value={loc.code}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-48 bg-slate-800 border-slate-700 text-white">
                <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mtd">Month to Date</SelectItem>
                <SelectItem value="qtd">Quarter to Date</SelectItem>
                <SelectItem value="ytd">Year to Date</SelectItem>
                <SelectItem value="ly">Last Year</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant={showAIInsights ? 'default' : 'outline'}
              size="sm"
              disabled
              title="Bolt AI is coming soon"
              aria-label="CFO AI Analysis — coming soon"
              className={showAIInsights 
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white' 
                : 'border-indigo-700 text-indigo-400 hover:bg-indigo-900/20'}
            >
              <Brain className="w-4 h-4 mr-2" />
              CFO AI Analysis
              <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${showAIInsights ? 'rotate-180' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        {/* AI Insights */}
        {showAIInsights && (
          <div className="mb-6">
            <AIInsightsPanel />
          </div>
        )}

        {/* Financial Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-slate-900 border border-slate-700">
            <TabsTrigger value="overview" className="data-[state=active]:bg-slate-700">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="pl" className="data-[state=active]:bg-slate-700">
              <FileText className="w-4 h-4 mr-2" />
              Profit & Loss
            </TabsTrigger>
            <TabsTrigger value="balance" className="data-[state=active]:bg-slate-700">
              <Briefcase className="w-4 h-4 mr-2" />
              Balance Sheet
            </TabsTrigger>
            <TabsTrigger value="cashflow" className="data-[state=active]:bg-slate-700">
              <CreditCard className="w-4 h-4 mr-2" />
              Cash Flow
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Executive KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <KPICard
                title="Total Revenue YTD"
                value="$927K"
                subtitle="Target: $1.95M"
                change={-52.4}
                icon={DollarSign}
                status="critical"
              />
              <KPICard
                title="Net Margin"
                value="-22.9%"
                subtitle="Target: 20%"
                change={-42.9}
                icon={TrendingDown}
                status="critical"
              />
              <KPICard
                title="COGS + COL"
                value="92.3%"
                subtitle="Target: 60%"
                change={32.3}
                icon={Package}
                status="critical"
              />
              <KPICard
                title="Cash in Bank"
                value="$31.9K"
                subtitle="Need: $118K"
                change={-73.0}
                icon={CreditCard}
                status="critical"
              />
            </div>

            {/* Revenue Mix */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-slate-900 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Revenue Mix</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <RePieChart>
                      <Pie
                        data={[
                          { name: 'Patient Collections', value: 629727, percent: 67.9 },
                          { name: 'Medical Insurance', value: 133468, percent: 14.4 },
                          { name: 'Vision Insurance', value: 155905, percent: 16.8 }
                        ]}
                        cx="50%"
                        cy="50%"
                        dataKey="value"
                        nameKey="name"
                        labelLine={false}
                        label={({ percent }) => `${percent}%`}
                        outerRadius={100}
                        fill="#8884d8"
                      >
                        <Cell fill="#10b981" />
                        <Cell fill="#3b82f6" />
                        <Cell fill="#8b5cf6" />
                      </Pie>
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                        formatter={(value: any) => `$${(value / 1000).toFixed(0)}K`}
                      />
                      <Legend />
                    </RePieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Location Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {financialData.locations.map(loc => (
                      <div key={loc.code} className="space-y-1">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-400">{loc.name}</span>
                          <span className={`text-sm font-medium ${
                            loc.margin > 0 ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {loc.margin}% margin
                          </span>
                        </div>
                        <Progress 
                          value={Math.max(0, (loc.revenue / financialData.totalRevenue) * 100)} 
                          className="h-2"
                        />
                        <div className="flex justify-between text-xs text-slate-500">
                          <span>${(loc.revenue / 1000).toFixed(0)}K revenue</span>
                          <span className={loc.netIncome > 0 ? 'text-green-400' : 'text-red-400'}>
                            ${(loc.netIncome / 1000).toFixed(0)}K net
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* P&L Waterfall */}
            <Card className="bg-slate-900 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">P&L Waterfall Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <WaterfallChart />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profit & Loss Tab */}
          <TabsContent value="pl" className="space-y-6">
            <Card className="bg-slate-900 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Detailed Profit & Loss Statement</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left py-3 text-slate-400">Category</th>
                        <th className="text-right py-3 text-slate-400">Amount</th>
                        <th className="text-right py-3 text-slate-400">% of Revenue</th>
                        <th className="text-right py-3 text-slate-400">Target %</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-slate-800">
                        <td className="py-3 font-medium text-white">Total Revenue</td>
                        <td className="text-right text-white">$927,150</td>
                        <td className="text-right text-slate-400">100.0%</td>
                        <td className="text-right text-slate-400">100.0%</td>
                      </tr>
                      <tr className="border-b border-slate-800">
                        <td className="py-3 pl-4 text-slate-300">Patient Collections</td>
                        <td className="text-right">$629,727</td>
                        <td className="text-right">67.9%</td>
                        <td className="text-right text-slate-500">60.0%</td>
                      </tr>
                      <tr className="border-b border-slate-800">
                        <td className="py-3 pl-4 text-slate-300">Insurance Collections</td>
                        <td className="text-right">$291,007</td>
                        <td className="text-right">31.4%</td>
                        <td className="text-right text-slate-500">40.0%</td>
                      </tr>
                      <tr className="border-b border-slate-700">
                        <td className="py-3 font-medium text-red-400">Cost of Labor</td>
                        <td className="text-right text-red-400">($495,189)</td>
                        <td className="text-right text-red-400">53.4%</td>
                        <td className="text-right text-green-400">29.0%</td>
                      </tr>
                      <tr className="border-b border-slate-700">
                        <td className="py-3 font-medium text-red-400">Cost of Goods Sold</td>
                        <td className="text-right text-red-400">($209,193)</td>
                        <td className="text-right text-red-400">22.6%</td>
                        <td className="text-right text-green-400">26.0%</td>
                      </tr>
                      <tr className="border-b border-slate-700 bg-slate-800/50">
                        <td className="py-3 font-medium text-yellow-400">Gross Profit</td>
                        <td className="text-right text-yellow-400">$222,768</td>
                        <td className="text-right text-yellow-400">24.0%</td>
                        <td className="text-right text-green-400">45.0%</td>
                      </tr>
                      <tr className="border-b border-slate-700">
                        <td className="py-3 font-medium text-red-400">Operating Expenses</td>
                        <td className="text-right text-red-400">($346,927)</td>
                        <td className="text-right text-red-400">37.4%</td>
                        <td className="text-right text-green-400">20.0%</td>
                      </tr>
                      <tr className="bg-slate-800">
                        <td className="py-3 font-bold text-red-500">Net Income</td>
                        <td className="text-right font-bold text-red-500">($212,057)</td>
                        <td className="text-right font-bold text-red-500">-22.9%</td>
                        <td className="text-right font-bold text-green-400">20.0%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Balance Sheet Tab */}
          <TabsContent value="balance" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="bg-slate-900 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-green-400">Assets</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-slate-400">Cash & Equivalents</p>
                      <p className="text-xl font-bold text-white">$31,900</p>
                      <p className="text-xs text-red-400">Need: $150,000</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-400">Accounts Receivable</p>
                      <p className="text-xl font-bold text-white">$184,000</p>
                      <p className="text-xs text-slate-500">DSO: 51 days</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-400">Total Current Assets</p>
                      <p className="text-xl font-bold text-white">$215,900</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-red-400">Liabilities</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-slate-400">Accounts Payable</p>
                      <p className="text-xl font-bold text-white">$97,500</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-400">Credit Card Debt</p>
                      <p className="text-xl font-bold text-white">$120,000</p>
                      <p className="text-xs text-red-400">High interest burden</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-400">Total Liabilities</p>
                      <p className="text-xl font-bold text-white">$217,500</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-yellow-400">Equity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-slate-400">Owner's Equity</p>
                      <p className="text-xl font-bold text-white">$210,457</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-400">Retained Earnings</p>
                      <p className="text-xl font-bold text-red-400">($212,057)</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-400">Total Equity</p>
                      <p className="text-xl font-bold text-red-400">($1,600)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Cash Flow Tab */}
          <TabsContent value="cashflow" className="space-y-6">
            <Card className="bg-slate-900 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Cash Flow Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="space-y-3">
                    <h4 className="text-green-400 font-medium">Operating Activities</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-400">Net Income</span>
                        <span className="text-red-400">($212,057)</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-400">A/R Change</span>
                        <span className="text-red-400">($15,000)</span>
                      </div>
                      <div className="flex justify-between font-medium border-t border-slate-700 pt-2">
                        <span>Operating Cash Flow</span>
                        <span className="text-red-400">($227,057)</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="text-blue-400 font-medium">Investing Activities</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-400">Equipment Purchase</span>
                        <span className="text-red-400">($25,000)</span>
                      </div>
                      <div className="flex justify-between font-medium border-t border-slate-700 pt-2">
                        <span>Investing Cash Flow</span>
                        <span className="text-red-400">($25,000)</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="text-purple-400 font-medium">Financing Activities</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-400">Credit Line Draw</span>
                        <span className="text-green-400">$50,000</span>
                      </div>
                      <div className="flex justify-between font-medium border-t border-slate-700 pt-2">
                        <span>Financing Cash Flow</span>
                        <span className="text-green-400">$50,000</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-slate-800 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-medium">Net Cash Flow</span>
                    <span className="text-2xl font-bold text-red-400">($202,057)</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    <AlertTriangle className="w-3 h-3 inline mr-1" />
                    Cash burn rate: $33,676/month - runway: 0.9 months
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default FinancialDashboard; 